import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import Logo from "./Logo";
import { GrSearch } from "react-icons/gr";
import { FaMedal, FaUser } from "react-icons/fa6";
import { HiShoppingBag, HiHeart } from "react-icons/hi2";
import { HiHome, HiCube, HiInformationCircle, HiWallet, HiPhone, HiUser, HiBars3, HiXMark, HiBell, HiUsers } from "react-icons/hi2";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setUserDetails } from "../store/userSlice";
import Context from "../context";
import debounceFunction from "../helpers/Debounce";
import { refreshPage } from "../helpers/refreshPage";
import ReferAFriend from "./ReferAFriend";
import CampaignModal from "./CampaignModal"
import { GlobalContext } from "../context/GlobalContext";
import { utils } from "../common/utils"
import usePWAInstall from "../hooks/usePWAInstall";
import { FiMic } from "react-icons/fi";
import { BiSearchAlt } from "react-icons/bi";
import { MdLogout, MdLogin, MdInstallMobile, MdCardGiftcard } from "react-icons/md";
import { useWishlist } from "../context/WishlistContext";
import DarkModeToggle from "./DarkModeToggle";
import { useTheme } from "../context/ThemeContext";
import useKeyboardShortcuts from "../hooks/useKeyboardShortcuts";
import KeyboardShortcutsHelp from "./KeyboardShortcutsHelp";
import { useNotification } from "../context/NotificationContext";
import NotificationDropdown from "./NotificationDropdown";
import useChatUnreadCount from "../hooks/useChatUnreadCount";
import { HiChatBubbleLeftRight } from "react-icons/hi2";

const Header = () => {
  const { canInstall, promptInstall } = usePWAInstall();

  const user = useSelector((state) => state?.user?.user);
  const { cartCount } = useSelector((state) => state?.cart);
  const { wishlistCount } = useWishlist();
  const { unreadCount } = useNotification();
  const { unreadCount: chatUnreadCount } = useChatUnreadCount();
  const { toggleTheme } = useTheme();
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  // Store the previous path before navigating to search

  const dispatch = useDispatch();
  const campaignModalRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const previousPathRef = useRef(location.pathname);
  const isClearingRef = useRef(false);

  const searchInput = useLocation();
  const getSearchQuery = () => {
    const params = new URLSearchParams(location.search);
    return params.get("q") || "";
  };
  const [search, setSearch] = useState(getSearchQuery());

  const handleLogout = async () => {
    localStorage.removeItem("_bnac");
    dispatch(setUserDetails(null));
    refreshPage();
    navigate("/");
  };

  const handleSearch = (e) => {
    const { value } = e.target;
    setSearch(value);
    debounceSearch(value);
  };

  const handleClearSearch = useCallback(() => {
    // Set flag to prevent useEffect from interfering
    isClearingRef.current = true;
    
    // Immediately clear the search state
    setSearch("");
    
    // Clear the URL query parameter
    if (location.pathname === "/search") {
      // If on search page, navigate back to previous page or home
      navigate(previousPathRef.current || "/", { replace: true });
    } else {
      // If on another page, just remove the query parameter from URL
      const newSearch = new URLSearchParams(location.search);
      newSearch.delete("q");
      const newUrl = newSearch.toString() 
        ? `${location.pathname}?${newSearch.toString()}`
        : location.pathname;
      navigate(newUrl, { replace: true });
    }
    
    // Reset flag after a short delay
    setTimeout(() => {
      isClearingRef.current = false;
    }, 100);
  }, [location.pathname, location.search, navigate]);


  const handleVoiceSearch = () => {
    if (!("webkitSpeechRecognition" in window)) {
      alert("Voice recognition is not supported on this browser.");
      return;
    }

    const recognition = new window.webkitSpeechRecognition();
    recognition.lang = "en-IN";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      console.log("Voice recognition started...");
    };

    recognition.onresult = (event) => {
      const voiceText = event.results[0][0].transcript;
      setSearch(voiceText); // Fill in the input
      debounceSearch(voiceText); // Trigger search
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event.error);
    };

    recognition.start();
  };

  const debounceSearch = useCallback(
    debounceFunction((query) => {
      if (query.trim() !== "") {
        previousPathRef.current = location.pathname;
        navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      }
      // Don't navigate away if search is empty - let user stay on current page
      // Only clear the search input value
    }, 1000),
    [location.pathname, navigate]
  );

  const [showMenu, setShowMenu] = useState(false);
  const toggleMenu = () => setShowMenu((prev) => !prev);

  const [isMobileSearchPanelOpen, setIsMobileSearchPanelOpen] = useState(false);
  const mobileSearchPanelInputRef = useRef(null);

  const [showReferModal, setShowReferModal] = useState(false);
  const toggleReferModal = () => setShowReferModal((prev) => !prev);

  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const toggleCampaignModal = () => setShowCampaignModal((prev) => !prev);

  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  const searchInputRef = useRef(null);


  const getInitials = (name) => (name ? name.charAt(0).toUpperCase() : "U");

  const menuRef = useRef(null);
  const referModalRef = useRef(null);

  const handleClickOutside = (event) => {
    // Check if the click is outside the menu
    if (menuRef.current && !menuRef.current.contains(event.target)) {
      setShowMenu(false);
    }

    // Check if the click is outside the refer modal
    if (referModalRef.current && !referModalRef.current.contains(event.target)) {
      setShowReferModal(false);
    }
  };


  // useEffect(() => {
  //   const handleClick = (event) => {
  //     const isMenuLinkClick =
  //       menuRef.current && menuRef.current.contains(event.target);
  //     const isReferModalLinkClick =
  //       referModalRef.current && referModalRef.current.contains(event.target);

  //     if (!isMenuLinkClick && !isReferModalLinkClick) {
  //       handleClickOutside(event);
  //     }
  //   };

  //   document.addEventListener("mousedown", handleClick);
  //   return () => {
  //     document.removeEventListener("mousedown", handleClick);
  //   };
  // }, []);

  // Sync search input with URL query parameter
  useEffect(() => {
    // Don't sync if we're in the middle of clearing
    if (isClearingRef.current) {
      return;
    }
    
    const query = getSearchQuery();
    // Only update if different to avoid unnecessary re-renders
    // But don't override if user is currently typing (search state is being updated)
    if (query !== search) {
      const isInputFocused = document.activeElement === searchInputRef.current;
      if (!isInputFocused) {
        setSearch(query);
      }
    }
  }, [location.search]);

  useEffect(() => {
    const handleClick = (event) => {
      const isMenuClick = menuRef.current?.contains(event.target);
      const isReferClick = referModalRef.current?.contains(event.target);
      const isCampaignClick = campaignModalRef.current?.contains(event.target);

      if (!isMenuClick && !isReferClick && !isCampaignClick) {
        setShowMenu(false);
        setShowReferModal(false);
        setShowCampaignModal(false); // 👈 This is new
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, []);


  const isActiveRoute = (path) => location.pathname === path;

  // Keyboard shortcuts
  const handleSearchFocus = useCallback(() => {
    const searchInput = document.querySelector('header input[type="text"]');
    if (searchInput) {
      searchInput.focus();
      searchInput.select();
    }
  }, []);

  const handleModalClose = useCallback(() => {
    setShowMenu(false);
    setShowKeyboardHelp(false);
  }, []);

  // Close mobile search panel when navigating/URL changes
  useEffect(() => {
    setIsMobileSearchPanelOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!isMobileSearchPanelOpen) return;
    // Focus the panel input so the user continues typing immediately.
    const t = setTimeout(() => {
      mobileSearchPanelInputRef.current?.focus();
      mobileSearchPanelInputRef.current?.select?.();
    }, 0);
    return () => clearTimeout(t);
  }, [isMobileSearchPanelOpen]);

  useKeyboardShortcuts({
    onSearchFocus: handleSearchFocus,
    onModalClose: handleModalClose,
    onHelpToggle: () => setShowKeyboardHelp(true),
    enabled: true,
  });

  return (
    <>
      <header className="h-16 bg-white fixed w-full z-40 border-b border-gray-200 shadow-sm dark:bg-gray-900 dark:border-gray-700">
        <div className="h-full container mx-auto flex items-center px-2 md:px-3 justify-between gap-2 md:gap-3">
        {/* Logo Section */}
        <Link to="/" className="flex-shrink-0 hover:opacity-80 transition-opacity">
          <Logo w={60} h={50} />
        </Link>

        {/* Search Section (Desktop only) */}
        <div className="hidden md:flex items-center w-full max-w-2xl px-4 py-2.5 gap-3 border-2 border-gray-300 rounded-full bg-gray-50 focus-within:border-orange-500 focus-within:bg-white focus-within:shadow-lg transition-all duration-300 hover:border-orange-400 dark:bg-gray-800 dark:border-gray-600 dark:focus-within:border-orange-400">
          <BiSearchAlt className="text-gray-500 dark:text-gray-400 text-lg flex-shrink-0" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search products... (Press / to focus)"
            className="flex-grow outline-none text-sm md:text-base bg-transparent placeholder-gray-500 dark:placeholder-gray-400 px-1 font-medium text-gray-900 dark:text-gray-100"
            onChange={handleSearch}
            value={search}
          />

          {/* Clear button - shows when search has text */}
          {search && search.trim() !== "" && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClearSearch();
              }}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-all duration-200 dark:text-gray-500 dark:hover:text-gray-300 dark:hover:bg-gray-700 flex-shrink-0"
              title="Clear search"
            >
              <HiXMark className="text-lg" />
            </button>
          )}

          <button
            title="Voice Search"
            onClick={handleVoiceSearch}
            className="p-2 text-green-600 hover:text-white hover:bg-gradient-to-r hover:from-green-500 hover:to-green-600 rounded-full transition-all duration-200 transform hover:scale-110 dark:text-green-400"
          >
            <FiMic className="text-base" />
          </button>

          <button
            onClick={() => debounceSearch(search)}
            className="p-2 text-orange-600 hover:text-white hover:bg-gradient-to-r hover:from-orange-500 hover:to-orange-600 rounded-full transition-all duration-200 transform hover:scale-110 dark:text-orange-400"
            title="Search"
          >
            <BiSearchAlt className="text-base" />
          </button>
        </div>


        {/* Mobile Search */}
        <div
          className={`md:hidden flex items-center min-w-0 flex-1 max-w-xs px-4 py-2.5 gap-3 border-2 border-gray-300 rounded-full bg-gray-50 focus-within:border-orange-500 focus-within:bg-white focus-within:shadow-lg transition-all duration-300 dark:bg-gray-800 dark:border-gray-600 dark:focus-within:border-orange-400 ${
            isMobileSearchPanelOpen ? "opacity-0 pointer-events-none" : ""
          }`}
        >
          <BiSearchAlt className="text-gray-500 dark:text-gray-400 text-base flex-shrink-0" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search..."
            className="flex-grow text-sm outline-none bg-transparent placeholder-gray-500 dark:placeholder-gray-400 min-w-0 px-1 font-medium text-gray-900 dark:text-gray-100"
            onChange={handleSearch}
            value={search}
            onFocus={() => setIsMobileSearchPanelOpen(true)}
          />
          
          {/* Clear button - shows when search has text */}
          {search && search.trim() !== "" && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClearSearch();
              }}
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent input from getting focus
              }}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-all duration-200 flex-shrink-0 dark:text-gray-500 dark:hover:text-gray-300 dark:hover:bg-gray-700 z-10"
              title="Clear search"
              aria-label="Clear search"
            >
              <HiXMark className="text-base" />
            </button>
          )}

          <button
            title="Voice Search"
            onClick={handleVoiceSearch}
            className="p-2 text-green-600 hover:text-white hover:bg-green-500 rounded-full transition-all duration-200 flex-shrink-0 dark:text-green-400"
          >
            <FiMic className="text-base" />
          </button>
        </div>

        {/* User Menu and Cart */}
        <div className="flex items-center gap-2 md:gap-3">
          {/* Cart */}
          {user?.id && (
            <Link
              to="/cart"
              className="relative group"
              title="Shopping Cart"
            >
              <div className="relative p-2 rounded-xl transition-all duration-300 hover:bg-gradient-to-br hover:from-orange-50 hover:to-yellow-50 group-hover:scale-110 transform">
                <HiShoppingBag className="text-xl text-orange-500 transition-colors duration-300 group-hover:text-orange-600" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white min-w-[24px] h-6 px-1.5 rounded-full flex items-center justify-center text-xs font-bold shadow-lg border-2 border-white">
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </div>
            </Link>
          )}

          {/* All navigation moved to dropdown menu to save space */}

          {/* User Avatar with Notification Badge */}
          {user?.id ? (
            <div className="relative">
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  // Toggle notification dropdown or menu
                  if (unreadCount > 0) {
                    setShowNotificationDropdown(!showNotificationDropdown);
                    setShowMenu(false);
                  } else {
                    toggleMenu();
                  }
                }}
                className={`relative w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-all duration-200 hover:scale-110 transform hover:ring-2 hover:ring-orange-200 ${user?.profileImage
                  ? "border-2 border-orange-200 ring-2 ring-orange-100"
                  : "bg-gradient-to-r from-orange-500 to-yellow-500 text-white shadow-md hover:shadow-lg"
                  }`}
              >
                {user?.profileImage ? (
                  <img
                    src={user?.profileImage}
                    alt="User"
                    className="w-10 h-10 rounded-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                ) : user?.fullName ? (
                  <span className="text-sm font-bold">{getInitials(user?.fullName)}</span>
                ) : (
                  <FaUser className="text-white text-lg" />
                )}
                
                {/* Notification Badge on Profile Picture */}
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-gradient-to-r from-red-500 to-pink-500 text-white min-w-[20px] h-5 px-1.5 rounded-full flex items-center justify-center text-xs font-bold shadow-lg border-2 border-white z-10">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </div>

              {/* Notification Dropdown */}
              {showNotificationDropdown && (
                <NotificationDropdown onClose={() => setShowNotificationDropdown(false)} />
              )}
            </div>
          ) : (
            <div className="relative">
              <div
                onClick={toggleMenu}
                className="relative w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-all duration-200 hover:scale-110 transform hover:ring-2 hover:ring-orange-200 bg-gradient-to-r from-orange-500 to-yellow-500 text-white shadow-md hover:shadow-lg"
              >
                <FaUser className="text-white text-lg" />
              </div>
            </div>
          )}

          {/* Dropdown Menu */}
          {showMenu && (
            <div
              ref={menuRef}
              className="absolute right-0 top-full mt-2 bg-white rounded-2xl shadow-2xl py-2 w-64 border border-gray-200 z-50 max-h-[calc(100vh-120px)] overflow-y-auto overflow-x-hidden"
            >
              <nav className="flex flex-col">
                <Link
                  to="/"
                  className={`flex items-center gap-3 px-4 py-3 text-gray-700 hover:text-orange-600 hover:bg-gradient-to-r hover:from-orange-50 hover:to-yellow-50 transition-all duration-200 font-medium ${
                    isActiveRoute("/") ? "text-orange-600 bg-gradient-to-r from-orange-50 to-yellow-50" : ""
                  }`}
                  onClick={() => setShowMenu(false)}
                >
                  <HiHome className="text-xl" />
                  <span>Home</span>
                </Link>
                {user?.id && (
                  <>
                    <Link
                      to="/order"
                      className={`flex items-center gap-3 px-4 py-3 text-gray-700 hover:text-orange-600 hover:bg-gradient-to-r hover:from-orange-50 hover:to-yellow-50 transition-all duration-200 font-medium ${
                        isActiveRoute("/order") ? "text-orange-600 bg-gradient-to-r from-orange-50 to-yellow-50" : ""
                      }`}
                      onClick={() => setShowMenu(false)}
                    >
                      <HiCube className="text-xl" />
                      <span>Orders</span>
                    </Link>
                    <Link
                      to="/wishlist"
                      className={`flex items-center gap-3 px-4 py-3 text-gray-700 hover:text-red-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 transition-all duration-200 font-medium ${
                        isActiveRoute("/wishlist") ? "text-red-600 bg-gradient-to-r from-red-50 to-pink-50" : ""
                      }`}
                      onClick={() => setShowMenu(false)}
                    >
                      <HiHeart className="text-xl" />
                      <span>Wishlist</span>
                      {wishlistCount > 0 && (
                        <span className="ml-auto bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                          {wishlistCount}
                        </span>
                      )}
                    </Link>
                    <Link
                      to="/wallet"
                      className={`flex items-center gap-3 px-4 py-3 text-gray-700 hover:text-orange-600 hover:bg-gradient-to-r hover:from-orange-50 hover:to-yellow-50 transition-all duration-200 font-medium ${
                        isActiveRoute("/wallet") ? "text-orange-600 bg-gradient-to-r from-orange-50 to-yellow-50" : ""
                      }`}
                      onClick={() => setShowMenu(false)}
                    >
                      <HiWallet className="text-xl" />
                      <span>Wallet</span>
                    </Link>
                    <Link
                      to="/notifications"
                      className={`flex items-center gap-3 px-4 py-3 text-gray-700 hover:text-orange-600 hover:bg-gradient-to-r hover:from-orange-50 hover:to-yellow-50 transition-all duration-200 font-medium ${
                        isActiveRoute("/notifications") ? "text-orange-600 bg-gradient-to-r from-orange-50 to-yellow-50" : ""
                      }`}
                      onClick={() => setShowMenu(false)}
                    >
                      <div className="relative">
                        <HiBell className="text-xl" />
                        {unreadCount > 0 && (
                          <span className="absolute -top-1.5 -right-1.5 bg-gradient-to-r from-red-500 to-pink-500 text-white min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center text-[10px] font-bold border-2 border-white">
                            {unreadCount > 99 ? '99+' : unreadCount}
                          </span>
                        )}
                      </div>
                      <span>Notifications</span>
                    </Link>
                    <Link
                      to="/chat"
                      className={`flex items-center gap-3 px-4 py-3 text-gray-700 hover:text-orange-600 hover:bg-gradient-to-r hover:from-orange-50 hover:to-yellow-50 transition-all duration-200 font-medium ${
                        isActiveRoute("/chat") ? "text-orange-600 bg-gradient-to-r from-orange-50 to-yellow-50" : ""
                      }`}
                      onClick={() => setShowMenu(false)}
                    >
                      <div className="relative">
                        <HiChatBubbleLeftRight className="text-xl" />
                        {chatUnreadCount > 0 && (
                          <span className="absolute -top-1.5 -right-1.5 bg-gradient-to-r from-blue-500 to-cyan-500 text-white min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center text-[10px] font-bold border-2 border-white">
                            {chatUnreadCount > 99 ? '99+' : chatUnreadCount}
                          </span>
                        )}
                      </div>
                      <span>Chat with Support</span>
                    </Link>
                    <Link
                      to="/profile"
                      className={`flex items-center gap-3 px-4 py-3 text-gray-700 hover:text-orange-600 hover:bg-gradient-to-r hover:from-orange-50 hover:to-yellow-50 transition-all duration-200 font-medium ${
                        isActiveRoute("/profile") ? "text-orange-600 bg-gradient-to-r from-orange-50 to-yellow-50" : ""
                      }`}
                      onClick={() => setShowMenu(false)}
                    >
                      <HiUser className="text-xl" />
                      <span>Profile</span>
                    </Link>
                    {utils?.referralDashboardSidebar && (
                      <Link
                        to="/referral-dashboard"
                        className={`flex items-center gap-3 px-4 py-3 text-gray-700 hover:text-orange-600 hover:bg-gradient-to-r hover:from-orange-50 hover:to-yellow-50 transition-all duration-200 font-medium ${
                          isActiveRoute("/referral-dashboard") ? "text-orange-600 bg-gradient-to-r from-orange-50 to-yellow-50" : ""
                        }`}
                        onClick={() => setShowMenu(false)}
                      >
                        <HiUsers className="text-xl" />
                        <span>Referral Dashboard</span>
                      </Link>
                    )}
                    {utils?.referAFriendSidebar && (
                      <div
                        className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:text-orange-600 hover:bg-gradient-to-r hover:from-orange-50 hover:to-yellow-50 cursor-pointer transition-all duration-200 font-medium"
                        onClick={() => {
                          setShowMenu(false);
                          toggleReferModal();
                        }}
                      >
                        <MdCardGiftcard className="text-xl" />
                        <span>Refer a Friend</span>
                      </div>
                    )}
                    <div
                      title="Active Campaign"
                      onClick={() => {
                        toggleCampaignModal();
                        setShowMenu(false);
                      }}
                      className="flex items-center gap-3 px-4 py-3 text-orange-600 hover:text-orange-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-yellow-50 cursor-pointer transition-all duration-200 font-medium"
                    >
                      <FaMedal className="text-xl" />
                      <span>Campaigns</span>
                      <span className="ml-auto bg-gradient-to-r from-orange-500 to-orange-600 text-white w-5 h-5 rounded-full text-[10px] flex items-center justify-center font-bold shadow-md">!</span>
                    </div>
                  </>
                )}
                <Link
                  to="/about"
                  className={`flex items-center gap-3 px-4 py-3 text-gray-700 hover:text-orange-600 hover:bg-gradient-to-r hover:from-orange-50 hover:to-yellow-50 transition-all duration-200 font-medium ${
                    isActiveRoute("/about") ? "text-orange-600 bg-gradient-to-r from-orange-50 to-yellow-50" : ""
                  }`}
                  onClick={() => setShowMenu(false)}
                >
                  <HiInformationCircle className="text-xl" />
                  <span>About</span>
                </Link>
                <Link
                  to="/contact"
                  className={`flex items-center gap-3 px-4 py-3 text-gray-700 hover:text-orange-600 hover:bg-gradient-to-r hover:from-orange-50 hover:to-yellow-50 transition-all duration-200 font-medium ${
                    isActiveRoute("/contact") ? "text-orange-600 bg-gradient-to-r from-orange-50 to-yellow-50" : ""
                  }`}
                  onClick={() => setShowMenu(false)}
                >
                  <HiPhone className="text-xl" />
                  <span>Contact</span>
                </Link>
                <div
                  className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:text-orange-600 hover:bg-gradient-to-r hover:from-orange-50 hover:to-yellow-50 cursor-pointer transition-all duration-200 font-medium"
                  onClick={() => {
                    promptInstall();
                    setShowMenu(false);
                  }}
                >
                  <MdInstallMobile className="text-xl" />
                  <span>Install App</span>
                </div>
                {/* Dark Mode Toggle (Mobile - in sidebar) */}
                <div 
                  className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 dark:hover:from-gray-800 dark:hover:to-gray-700 transition-all duration-200 font-medium cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleTheme();
                    setTimeout(() => setShowMenu(false), 150);
                  }}
                >
                  <div className="flex items-center justify-center w-8 h-8">
                    <DarkModeToggle className="!w-full !h-full pointer-events-none" />
                  </div>
                  <span>Dark Mode</span>
                </div>
              </nav>
              <div className="mt-2 pt-2 border-t-2 border-gray-200 dark:border-gray-700">
                {user?.id ? (
                  <div
                    className="flex items-center gap-3 px-4 py-3 text-red-600 hover:text-red-700 hover:bg-red-50 cursor-pointer transition-all duration-200 font-semibold"
                    onClick={() => {
                      handleLogout();
                      setShowMenu(false);
                    }}
                  >
                    <MdLogout className="text-xl" />
                    <span>Logout</span>
                  </div>
                ) : (
                  <Link
                    to="/login"
                    className="flex items-center gap-3 px-4 py-3 text-green-600 hover:text-green-700 hover:bg-green-50 transition-all duration-200 font-semibold"
                    onClick={() => setShowMenu(false)}
                  >
                    <MdLogin className="text-xl" />
                    <span>Login</span>
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Refer Modal */}
      {showReferModal && user && (
        <div ref={referModalRef} className="absolute top-16 right-4 bg-white rounded-lg shadow-lg p-4 z-50">
          <ReferAFriend user={user} handleToggle={toggleReferModal} />
        </div>
      )}

      {/* Campaign Modal */}
      {/* {showCampaignModal && (
        <CampaignModal
          toggleCampaignModal={toggleCampaignModal}
          setShowCampaignModal={setShowCampaignModal}
          user={user}
        />
      )} */}

      {showCampaignModal && (
        <div ref={campaignModalRef} >
          <CampaignModal
            toggleCampaignModal={toggleCampaignModal}
            setShowCampaignModal={setShowCampaignModal}
            user={user}
          />
        </div>
      )}

      {/* Keyboard Shortcuts Help Modal */}
      <KeyboardShortcutsHelp
        isOpen={showKeyboardHelp}
        onClose={() => setShowKeyboardHelp(false)}
      />
      </header>

      {/* Mobile search panel - appears below header */}
      {isMobileSearchPanelOpen && (
        <>
          <div
            className="fixed inset-0 z-[69] md:hidden"
            onClick={() => setIsMobileSearchPanelOpen(false)}
          />
          <div className="fixed left-0 right-0 top-16 z-[70] md:hidden bg-white border-b border-gray-200 px-4 py-3 shadow-lg">
            <div className="flex items-center min-w-0 w-full px-4 py-2 gap-3 border-2 border-gray-300 rounded-full bg-gray-50 focus-within:border-orange-500 focus-within:bg-white transition-all dark:bg-gray-800 dark:border-gray-600 dark:focus-within:border-orange-400">
              <BiSearchAlt className="text-gray-500 dark:text-gray-400 text-base flex-shrink-0" />
              <input
                ref={mobileSearchPanelInputRef}
                type="text"
                placeholder="Search..."
                className="flex-grow text-sm outline-none bg-transparent placeholder-gray-500 dark:placeholder-gray-400 min-w-0 px-1 font-medium text-gray-900 dark:text-gray-100"
                onChange={handleSearch}
                value={search}
              />

              {search && search.trim() !== "" && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleClearSearch();
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-all duration-200 flex-shrink-0 dark:text-gray-500 dark:hover:text-gray-300 dark:hover:bg-gray-700 z-10"
                  title="Clear search"
                  aria-label="Clear search"
                >
                  <HiXMark className="text-base" />
                </button>
              )}

              <button
                title="Voice Search"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleVoiceSearch();
                }}
                className="p-2 text-green-600 hover:text-white hover:bg-green-500 rounded-full transition-all duration-200 flex-shrink-0 dark:text-green-400"
              >
                <FiMic className="text-base" />
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Header;
